import { useState } from "react";
import dayjs from "dayjs";
import { X, Megaphone, FileText, Link as LinkIcon } from "lucide-react";
import { getUserColor, getInitials } from "../utils/avatarColor";
import "./ChatInfoPanel.css";

export default function ChatInfoPanel({
  user,
  media = [],
  docs = [],
  links = [],
  onClose,
  onJump,
  onDownload,
}) {
  const [tab, setTab] = useState("media"); // media | docs | links
  const isGroup = String(user?._id || "").startsWith("group:announcements");

  const tabs = [
    { key: "media", label: "Media", count: media.length },
    { key: "docs", label: "Docs", count: docs.length },
    { key: "links", label: "Links", count: links.length },
  ];

  return (
    <aside className="cip">
      <div className="cip__top">
        <button className="cip__close" onClick={onClose} aria-label="Close">
          <X size={20} />
        </button>
        <span>{isGroup ? "Group info" : "Contact info"}</span>
      </div>

      <div className="cip__body">
        {/* Profile */}
        <div className="cip__profile">
          <div
            className="cip__avatar"
            style={{
              backgroundColor: isGroup
                ? "#f59e0b"
                : user?.profilePic
                  ? "transparent"
                  : getUserColor(user?._id),
            }}
          >
            {isGroup ? (
              <Megaphone size={38} />
            ) : user?.profilePic ? (
              <img src={user.profilePic} alt={user?.name} />
            ) : (
              getInitials(user?.name)
            )}
          </div>
          <div className="cip__name">{user?.name}</div>
          {user?.email && <div className="cip__sub">{user.email}</div>}
        </div>

        {/* Media, links and docs */}
        <div className="cip__section-title">
          <span>Media, links and docs</span>
          <span>{media.length + docs.length + links.length}</span>
        </div>

        <div className="cip__tabs">
          {tabs.map((t) => (
            <button
              key={t.key}
              className={`cip__tab ${tab === t.key ? "is-active" : ""}`}
              onClick={() => setTab(t.key)}
            >
              {t.label} ({t.count})
            </button>
          ))}
        </div>

        {/* MEDIA GRID */}
        {tab === "media" &&
          (media.length ? (
            <div className="cip__grid">
              {media.map((m) => (
                <a
                  key={m.id}
                  className="cip__thumb"
                  href={m.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={m.name}
                >
                  <img src={m.url} alt={m.name} loading="lazy" />
                </a>
              ))}
            </div>
          ) : (
            <div className="cip__empty">No media shared yet</div>
          ))}

        {/* DOCS LIST */}
        {tab === "docs" &&
          (docs.length ? (
            <div className="cip__list">
              {docs.map((d) => (
                <div key={d.id} className="cip__row">
                  <div className="cip__row-icon">
                    <FileText size={20} />
                  </div>
                  <div
                    className="cip__row-info"
                    onClick={() => onJump?.(d.messageId)}
                  >
                    <div className="cip__row-title">{d.name}</div>
                    <div className="cip__row-sub">
                      {dayjs(d.createdAt).format("DD/MM/YYYY")}
                    </div>
                  </div>
                  <a
                    className="cip__row-act"
                    href={d.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Preview
                  </a>
                  <button
                    className="cip__row-act"
                    onClick={() => onDownload?.(d.norm, d.name)}
                  >
                    Download
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="cip__empty">No documents shared yet</div>
          ))}

        {/* LINKS LIST */}
        {tab === "links" &&
          (links.length ? (
            <div className="cip__list">
              {links.map((l) => (
                <div key={l.id} className="cip__row">
                  <div className="cip__row-icon">
                    <LinkIcon size={20} />
                  </div>
                  <div
                    className="cip__row-info"
                    onClick={() => onJump?.(l.messageId)}
                  >
                    <a
                      className="cip__row-title cip__link"
                      href={l.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {l.url}
                    </a>
                    <div className="cip__row-sub">
                      {dayjs(l.createdAt).format("DD/MM/YYYY")}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="cip__empty">No links shared yet</div>
          ))}
      </div>
    </aside>
  );
}
